import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { notNull } from '~/_shared/utils/typeGuards';
import { SidebarApp } from '~/sidebar/sidebarApp.enum';
import { useIsBoundaryToolReady } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/edit/useIsBoundaryToolReady';
import { type AppState } from '~/store/app.store';
import { useBTGsGroupedByBoundaryGroupSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  useFrontendStateLeftSidebarOpenedAppSelector, useFrontendStateLeftSidebarOpenedMapToolSelector,
} from '~/store/frontendState/leftSidebar/leftSidebar.selectors';
import { useBoundarySelectIsLassoLoadingSelector } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.selectors';
import { useBoundaryConvertInProgressSelector } from '~/store/frontendState/mapTools/boundary/convertBoundaries/convertBoundaries.selectors';
import { isWeightedCenterActiveSelector } from '~/store/frontendState/mapTools/weightedCenter/weightedCenter.selectors';
import { OverMapLoaderComponent } from './overMapLoader.component';
import { useBoundaryLoading } from './useBoundaryLoading';

const isMapTopViewBlocked = (state: AppState): boolean =>
  state.frontendState.moveMarkers.isModalOpen
    || state.frontendState.mapTools.search.searchActive
    || state.frontendState.mapTools.boundary.boundaryDraw.isModalOpen
    || state.frontendState.mapTools.boundary.boundarySelect.isModalOpen
    || notNull(state.frontendState.mapTools.drawing.drawingTool.activeTool)
    || notNull(state.frontendState.mapTools.drawing.drawingEdit.selectedDrawing)
    || isWeightedCenterActiveSelector(state);

export const useIsMapTopViewBlocked = () => useSelector(isMapTopViewBlocked);

const boundaryTools = new Set<MapTool | null>([MapTool.Boundary, MapTool.MapLegend, MapTool.DataLayers]);

export const OverMapLoaderContainer: React.FC = () => {
  const [t] = useTranslation();
  const position = useIsMapTopViewBlocked() ? 'bottom' : 'top';

  const loadingBoundaries = useBoundaryLoading();
  const convertBoundariesInProgress = useBoundaryConvertInProgressSelector();
  const isBoundarySelectLassoLoading = useBoundarySelectIsLassoLoadingSelector();
  const mapBtgs = useBTGsGroupedByBoundaryGroupSelector();
  const openedApp = useFrontendStateLeftSidebarOpenedAppSelector();
  const openedMapTool = useFrontendStateLeftSidebarOpenedMapToolSelector();
  const { isBoundaryToolPreparing } = useIsBoundaryToolReady();

  const isBoundaryToolOpen = openedApp === SidebarApp.MapTools && boundaryTools.has(openedMapTool);
  const mapHasBTGs = mapBtgs.all.length > 0;

  // Register your loaders below. Only one should be displayed at a time.

  if (convertBoundariesInProgress) {
    return (
      <OverMapLoaderComponent
        position={position}
        titleText={t('Generating New Territories')}
        subTitleText={t('This may take a minute...')}
      />
    );
  }

  if (isBoundaryToolPreparing) {
    if (isBoundaryToolOpen || mapHasBTGs) {
      return (
        <OverMapLoaderComponent
          position={position}
          titleText={t('boundaryTool.preparing.title')}
          subTitleText={t('boundaryTool.preparing.description')}
        />
      );
    }
    return null;
  }

  if (loadingBoundaries && !isBoundaryToolPreparing) {
    return (
      <OverMapLoaderComponent
        position={position}
        titleText={t('Loading Your Boundaries')}
        subTitleText={t('This may take a minute...')}
      />
    );
  }

  if (isBoundarySelectLassoLoading) {
    return (
      <OverMapLoaderComponent
        position={position}
        titleText={t('Loading Your Selection')}
        subTitleText={t('This may take a minute...')}
      />
    );
  }

  return null;
};
